import React, { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, CheckCircle, XCircle, Trophy, X, BookOpen } from "lucide-react";

const API_BASE = "https://prdp-api.suport-949.workers.dev/api";

export default function CourseViewer({ courseId, memberId, onExit }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [slideIndex, setSlideIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [showQuestions, setShowQuestions] = useState(false);
  const [completed, setCompleted] = useState(false);

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerStatus, setAnswerStatus] = useState(null); // "correct", "wrong", or null

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const courseRes = await fetch(`${API_BASE}/cursuri/${courseId}`, {
          headers: { Authorization: "Bearer unwantedtok" },
        });
        if (!courseRes.ok) throw new Error("Nu s-a putut încărca cursul.");
        const data = await courseRes.json();

        const progressRes = await fetch(
          `${API_BASE}/cursuri/${courseId}/progres/${memberId}`,
          { headers: { Authorization: "Bearer unwantedtok" } }
        );
        if (!progressRes.ok)
          throw new Error("Nu s-a putut încărca progresul cursului.");

        const progressData = await progressRes.json();

        setCourse({
          title: data.curs?.titlu || "",
          difficulty: data.curs?.dificultate || "",
          duration: data.curs?.timp_estimativ || "",
          slides: data.slideuri || [],
          questions: data.intrebari || [],
        });

        if (progressData) {
          setSlideIndex(progressData.last_slide ?? 0);
          setQuestionIndex(progressData.last_question ?? 0);
          setShowQuestions(
            progressData.last_slide >= ((data.slideuri?.length ?? 0) - 1)
          );
          setCompleted(progressData.finalizat === 1);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [courseId, memberId]);

  async function saveProgress(slide, question, finalized = 0) {
    try {
      await fetch(`${API_BASE}/cursuri/${courseId}/progres`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer unwantedtok",
        },
        body: JSON.stringify({
          id_membru: memberId,
          last_slide: slide,
          last_question: question,
          finalizat: finalized,
        }),
      });
    } catch (e) {
      console.error("Eroare la salvarea progresului", e);
    }
  }

  function handleNextSlide() {
    if (!course) return;
    const next = slideIndex + 1;
    if (next < (course.slides?.length ?? 0)) {
      setSlideIndex(next);
      saveProgress(next, questionIndex, 0);
    } else {
      // Dacă am ajuns la ultimul slide, trecem la întrebări
      setShowQuestions(true);
      saveProgress(slideIndex, questionIndex, 0);
    }
  }

  function handleBackSlide() {
    if (!course) return;
    if (slideIndex > 0) {
      const prev = slideIndex - 1;
      setSlideIndex(prev);
      saveProgress(prev, questionIndex, 0);
    }
  }

  function handleNextQuestion() {
    if (!course) return;
    const nextQ = questionIndex + 1;
    if (nextQ < (course.questions?.length ?? 0)) {
      setQuestionIndex(nextQ);
      saveProgress(slideIndex, nextQ, 0);
      setSelectedAnswer(null);
      setAnswerStatus(null);
    }
  }

  function handleBackQuestion() {
    if (questionIndex > 0) {
      const prevQ = questionIndex - 1;
      setQuestionIndex(prevQ);
      saveProgress(slideIndex, prevQ, 0);
      setSelectedAnswer(null);
      setAnswerStatus(null);
    } else {
      setShowQuestions(false);
    }
  }

  function handleAnswer(answer) {
    if (completed || answerStatus !== null) return;

    const currentQ = course.questions[questionIndex];
    setSelectedAnswer(answer);

    if (answer === currentQ.raspuns_corect) {
      setAnswerStatus("correct");

      const nextQuestion = questionIndex + 1;
      const finalized = nextQuestion >= (course.questions?.length ?? 0);

      setTimeout(() => {
        setAnswerStatus(null);
        setSelectedAnswer(null);

        if (finalized) {
          setCompleted(true);
          saveProgress(slideIndex, questionIndex, 1);
        } else {
          handleNextQuestion();
        }
      }, 1200); // 1.2 secunde delay ca să vadă că a răspuns corect
    } else {
      setAnswerStatus("wrong");
      setTimeout(() => {
        setAnswerStatus(null);
        setSelectedAnswer(null);
      }, 1500); // Resetează răspunsul greșit ca să poată încerca din nou
    }
  }

  // UI STATES
  if (loading) return <div style={{ color: "#94a3b8", textAlign: "center", padding: "40px" }}>Se încarcă cursul...</div>;
  if (error) return <div style={{ color: "#ef4444", textAlign: "center", padding: "40px" }}>Eroare: {error}</div>;
  if (!course) return <div style={{ color: "#94a3b8", textAlign: "center", padding: "40px" }}>Cursul nu a fost găsit.</div>;

  // Calculăm progresul global
  const totalSteps = (course.slides?.length || 0) + (course.questions?.length || 0);
  const currentStep = showQuestions 
    ? (course.slides?.length || 0) + questionIndex + 1 
    : slideIndex + 1;
  const progressPercent = completed ? 100 : Math.round((currentStep / totalSteps) * 100);

  // ECRAN FINALIZARE
  if (completed) {
    return (
      <div style={{ padding: "40px 20px", maxWidth: 700, margin: "40px auto", backgroundColor: "#1e293b", borderRadius: "24px", textAlign: "center", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
        <Trophy size={64} color="#fbbf24" style={{ marginBottom: "24px", filter: "drop-shadow(0 0 12px rgba(251, 191, 36, 0.4))" }} />
        <h2 style={{ color: "#f8fafc", fontWeight: 800, marginBottom: "16px" }}>Felicitări!</h2>
        <p style={{ color: "#cbd5e1", fontSize: "1.1rem", marginBottom: "32px" }}>Ai finalizat cu succes cursul <strong>{course.title}</strong> și ai obținut 20 de puncte de activitate PRDP.</p>
        <button
          onClick={onExit}
          style={{ backgroundColor: "#fbbf24", color: "#0f172a", border: "none", borderRadius: "12px", padding: "14px 32px", fontWeight: 700, fontSize: "1rem", cursor: "pointer", transition: "all 0.2s ease" }}
        >
          Înapoi la Bibliotecă
        </button>
      </div>
    );
  }

  // UI PRINCIPAL
  return (
    <div
      style={{
        background: "#1e293b",
        borderRadius: "24px",
        maxWidth: 750,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.4)",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        margin: "0 auto",
        userSelect: "none",
        overflow: "hidden"
      }}
    >
      {/* HEADER CURS & PROGRESS BAR */}
      <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(255, 255, 255, 0.05)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ backgroundColor: "rgba(251, 191, 36, 0.1)", padding: "8px", borderRadius: "10px" }}>
            <BookOpen size={20} color="#fbbf24" />
          </div>
          <div>
            <h4 style={{ color: "#f8fafc", margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>{course.title}</h4>
            <div style={{ color: "#94a3b8", fontSize: "0.8rem", marginTop: "2px" }}>
              {showQuestions ? "Test de evaluare" : "Material de studiu"} • Pasul {currentStep} din {totalSteps}
            </div>
          </div>
        </div>
        <button 
          onClick={onExit}
          style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer" }}
          title="Închide cursul"
        >
          <X size={24} />
        </button>
      </div>

      {/* PROGRESS BAR STRIP */}
      <div style={{ width: "100%", height: "4px", backgroundColor: "#0f172a" }}>
        <div style={{ width: `${progressPercent}%`, height: "100%", backgroundColor: "#fbbf24", transition: "width 0.4s ease-out" }}></div>
      </div>

      {/* CONȚINUT */}
      <div style={{ padding: "32px 24px" }}>
        
        {/* VIEW SLIDE */}
        {!showQuestions ? (
          <div>
            <div style={{ color: "#fbbf24", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "16px" }}>
              Slide {slideIndex + 1}
            </div>
            <p style={{ fontSize: "1.15rem", color: "#e2e8f0", lineHeight: "1.7", marginBottom: "40px", whiteSpace: "pre-wrap" }}>
              {course.slides[slideIndex]?.continut || ""}
            </p>
            
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button
                onClick={handleBackSlide}
                disabled={slideIndex === 0}
                style={{
                  padding: "12px 20px", display: "flex", alignItems: "center", gap: "8px",
                  fontSize: "0.95rem", fontWeight: 600, borderRadius: "12px", border: "1px solid rgba(255,255,255,0.1)",
                  cursor: slideIndex === 0 ? "not-allowed" : "pointer",
                  backgroundColor: slideIndex === 0 ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.05)",
                  color: slideIndex === 0 ? "#475569" : "#cbd5e1",
                  transition: "all 0.2s ease"
                }}
              >
                <ArrowLeft size={18} /> Înapoi
              </button>
              <button
                onClick={handleNextSlide}
                style={{
                  padding: "12px 24px", display: "flex", alignItems: "center", gap: "8px",
                  fontSize: "0.95rem", fontWeight: 700, borderRadius: "12px", border: "none",
                  cursor: "pointer", backgroundColor: "#fbbf24", color: "#0f172a",
                  transition: "all 0.2s ease"
                }}
              >
                Continuă <ArrowRight size={18} />
              </button>
            </div>
          </div>
        ) : (
          /* VIEW ÎNTREBĂRI */
          <div>
            <div style={{ color: "#fbbf24", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "16px" }}>
              Întrebarea {questionIndex + 1}
            </div>
            <p style={{ fontSize: "1.2rem", color: "#f8fafc", fontWeight: 600, marginBottom: "32px", lineHeight: "1.5" }}>
              {course.questions[questionIndex]?.intrebare || ""}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }}>
              {["a", "b", "c", "d", "e"].map((opt) => {
                const q = course.questions[questionIndex];
                if (!q || !q[`varianta_${opt}`]) return null;
                
                // Calculăm stilul în funcție de starea răspunsului
                const isSelected = selectedAnswer === opt;
                let bgColor = "#0f172a";
                let borderColor = "rgba(255, 255, 255, 0.05)";
                let textColor = "#cbd5e1";
                let Icon = null;

                if (isSelected) {
                  if (answerStatus === "correct") {
                    bgColor = "rgba(16, 185, 129, 0.1)";
                    borderColor = "#10b981";
                    textColor = "#10b981";
                    Icon = <CheckCircle size={20} color="#10b981" />;
                  } else if (answerStatus === "wrong") {
                    bgColor = "rgba(239, 68, 68, 0.1)";
                    borderColor = "#ef4444";
                    textColor = "#ef4444";
                    Icon = <XCircle size={20} color="#ef4444" />;
                  } else {
                    bgColor = "rgba(251, 191, 36, 0.1)";
                    borderColor = "#fbbf24";
                    textColor = "#fbbf24";
                  }
                }

                return (
                  <button
                    key={opt}
                    onClick={() => handleAnswer(opt)}
                    disabled={answerStatus !== null}
                    style={{
                      width: "100%", padding: "16px 20px", fontSize: "1rem", borderRadius: "12px",
                      cursor: answerStatus ? "default" : "pointer",
                      backgroundColor: bgColor, border: `1px solid ${borderColor}`, color: textColor,
                      fontWeight: "500", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !answerStatus) e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !answerStatus) e.currentTarget.style.backgroundColor = "#0f172a";
                    }}
                  >
                    <span><strong style={{ opacity: 0.7, marginRight: "8px" }}>{opt.toUpperCase()}.</strong> {q[`varianta_${opt}`]}</span>
                    {Icon}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button
                onClick={handleBackQuestion}
                disabled={questionIndex === 0 && (course.slides?.length ?? 0) === 0}
                style={{
                  padding: "12px 20px", display: "flex", alignItems: "center", gap: "8px",
                  fontSize: "0.95rem", fontWeight: 600, borderRadius: "12px", border: "1px solid rgba(255,255,255,0.1)",
                  cursor: (questionIndex === 0 && (course.slides?.length ?? 0) === 0) ? "not-allowed" : "pointer",
                  backgroundColor: (questionIndex === 0 && (course.slides?.length ?? 0) === 0) ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.05)",
                  color: (questionIndex === 0 && (course.slides?.length ?? 0) === 0) ? "#475569" : "#cbd5e1",
                  transition: "all 0.2s ease"
                }}
              >
                <ArrowLeft size={18} /> Înapoi
              </button>

              {answerStatus === "wrong" && (
                <span style={{ color: "#ef4444", fontWeight: "600", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: "6px", animation: "fadeIn 0.3s ease" }}>
                  <XCircle size={18} /> Mai încearcă o dată
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
